import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import BeforeFooter from '../../components/layout/BeforeFooter';
import Bookselector from './Bookselector';
import Bookpreview from './Bookpreview';
import Formatselector from './Formatselector';
import Contactform from './Contactform';
import Paymentbox from './Paymentbox';
import Uploadbox from './Uploadbox';
import Bundlesection from './Bundlesection';
import Orderconfirmationmodal from './Orderconfirmationmodal';
import Preordermodal from './Preordermodal';
import { BOOKS, bundleCovers, getTextColor } from './Bookconfig';
import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE_MB } from './Constants';
import { isValidEmail } from './Utils';
import { API_URL } from '../../config/api';
import { ALGERIA_LOCATIONS } from '../../data/wilayas/AlgeriaLocations';
import { DELIVERY_FEES } from '../../data/wilayas/DeliveryFees';
import boxGift from '../../assets/box_gift.png';
import './order.css';

// Currency label used across the price box and sticky summary.
// Change this in one place if you need to switch it (e.g. to "DZD").
const CURRENCY = 'DA';

function OrderPage() {
  const [bookId, setBookId] = useState('');
  const [format, setFormat] = useState('digital');
  const [quantity, setQuantity] = useState(1);
  const [imageIndex, setImageIndex] = useState(0);
  const [proof, setProof] = useState(null);
  const [proofError, setProofError] = useState('');
  const [digitalType, setDigitalType] = useState('pdf');
  const [wilaya, setWilaya] = useState('');
  const [commune, setCommune] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponStatus, setCouponStatus] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const couponTimerRef = useRef(null);
  const couponAbortRef = useRef(null);
  const [language, setLanguage] = useState('en');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('home');

  // Preorder state
  const [showPreorder, setShowPreorder] = useState(false);
  const [preorderFirstName, setPreorderFirstName] = useState('');
  const [preorderEmail, setPreorderEmail] = useState('');
  const [preorderSubmitted, setPreorderSubmitted] = useState(false);
  const [preorderLoading, setPreorderLoading] = useState(false);
  const [preorderError, setPreorderError] = useState('');
  const [preorderBook, setPreorderBook] = useState('');

  const communes = wilaya ? ALGERIA_LOCATIONS[wilaya] || [] : [];
  const orderSectionRef = useRef(null);
  const fileInputRef = useRef(null);
  const { bookSlug } = useParams();

  const book = BOOKS.find((b) => b.id === bookId);
  const themeColor = useMemo(() => book?.color || '#2d346c', [book]);
  const textColor = useMemo(() => getTextColor(themeColor), [themeColor]);
  const isPaperbackComingSoon = book?.paperbackComingSoon || book?.isBundle;

  const deliveryFee = useMemo(() => {
    if (format !== 'paper' || !wilaya) return null;
    const fees = DELIVERY_FEES[wilaya];
    if (!fees) return null;
    return deliveryMethod === 'home' ? fees.home : fees.office;
  }, [format, wilaya, deliveryMethod]);

  const images = book
    ? format === 'paper'
      ? [book.paperFront, book.paperBack]
      : [language === 'ar' && book.arabicDigitalCover ? book.arabicDigitalCover : book.digitalCover]
    : [];

  const clearError = (field) => {
    if (errors[field]) setErrors((prev) => { const next = { ...prev }; delete next[field]; return next; });
  };

  // ── Reset entire form after confirmed order ──
  const resetForm = () => {
    setBookId('');
    setFormat('digital');
    setQuantity(1);
    setImageIndex(0);
    setProof(null);
    setProofError('');
    setDigitalType('pdf');
    setWilaya('');
    setCommune('');
    setCouponCode('');
    setCouponStatus(null);
    setCouponDiscount(0);
    setLanguage('en');
    setErrors({});
    setSubmitError('');
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setDeliveryMethod('home');
  };

  const closeConfirmation = () => {
    setShowConfirmation(false);
    resetForm();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCouponChange = (e) => {
    const value = e.target.value;
    setCouponCode(value);
    setCouponStatus(null);
    setCouponDiscount(0);

    if (couponTimerRef.current) clearTimeout(couponTimerRef.current);
    if (couponAbortRef.current) couponAbortRef.current.abort();
    if (!value.trim()) return;

    couponTimerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      couponAbortRef.current = controller;
      try {
        const res = await fetch(`${API_URL}/coupons/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: value.trim(), format }),
          signal: controller.signal,
        });
        if (!res.ok) throw new Error('Server error');
        const data = await res.json();
        if (data.valid) {
          setCouponStatus('valid');
          setCouponDiscount(data.discount);
        } else if (data.reason === 'digital_only') {
          setCouponStatus('digital_only');
        } else {
          setCouponStatus('invalid');
        }
      } catch (err) {
        if (err.name === 'AbortError') return; // superseded by a newer keystroke, ignore
        setCouponStatus('network_error');
      }
    }, 600);
  };

  // Cancel any pending debounce timer / in-flight request if the page unmounts mid-check
  useEffect(() => {
    return () => {
      clearTimeout(couponTimerRef.current);
      if (couponAbortRef.current) couponAbortRef.current.abort();
    };
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setProofError('Please upload an image (JPG, PNG, WEBP) or PDF file.');
      setProof(null);
      return;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setProofError(`File is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
      setProof(null);
      return;
    }
    setProofError('');
    setProof(file);
    clearError('proof');
  };

  const handlePreorderSubmit = async () => {
    if (!preorderFirstName.trim() || !preorderEmail.trim() || !isValidEmail(preorderEmail)) return;
    setPreorderLoading(true);
    setPreorderError('');
    try {
      const res = await fetch(`${API_URL}/preorders/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: preorderFirstName,
          email: preorderEmail,
          book: preorderBook,
        }),
      });
      if (!res.ok) throw new Error('Server error');
      setPreorderSubmitted(true);
    } catch {
      setPreorderError('Something went wrong. Please try again or contact us on WhatsApp.');
    } finally {
      setPreorderLoading(false);
    }
  };

  const closePreorder = () => {
    setShowPreorder(false);
    setPreorderSubmitted(false);
    setPreorderFirstName('');
    setPreorderEmail('');
    setPreorderBook('');
    setPreorderError('');
  };

  const openPreorder = (bookLabel) => {
    setPreorderBook(bookLabel);
    setShowPreorder(true);
  };

  const price = book
    ? format === 'digital'
      ? Math.round(book.priceDigital * (1 - couponDiscount))
      : book.pricePaper * quantity
    : 0;

  useEffect(() => {
    if (bookSlug) {
      if (bookSlug === 'bwgo-ar') {
        setBookId('bwgo');
        setLanguage('ar');
      } else {
        setBookId(bookSlug);
        setLanguage('en');
      }
      setFormat('digital');
      setQuantity(1);
      setImageIndex(0);
      orderSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [bookSlug]);

  const validate = () => {
    const newErrors = {};
    if (!firstName.trim()) newErrors.firstName = true;
    if (!lastName.trim()) newErrors.lastName = true;
    if (!email.trim() || !isValidEmail(email)) newErrors.email = true;
    if (format === 'paper') {
      if (!phone.trim() || phone.trim().length < 9) newErrors.phone = true;
      if (!wilaya) newErrors.wilaya = true;
      if (!commune) newErrors.commune = true;
    }
    if (!proof) newErrors.proof = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Progress, surfaced inline in the sticky summary instead of a separate bar ──
  const detailsComplete =
    !!firstName.trim() &&
    !!lastName.trim() &&
    isValidEmail(email) &&
    (format !== 'paper' || (phone.trim().length >= 9 && !!wilaya && !!commune));
  const proofComplete = !!proof;

  // Bookselector calls this with the chosen book's id directly (not an event).
  const handleBookChange = (id) => {
    setBookId(id);
    setImageIndex(0);
    setFormat('digital');
    setQuantity(1);
    setLanguage('en');
    setErrors({});
    setSubmitError('');
    setCouponCode('');
    setCouponStatus(null);
    setCouponDiscount(0);
    setProof(null);
    setProofError('');
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    setFormat('digital');
  };

  const handleSelectDigital = () => {
    setFormat('digital');
    setQuantity(1);
    setDigitalType('pdf');
  };

  const handleSelectPaper = () => {
    if (isPaperbackComingSoon) {
      openPreorder(
        book?.isBundle
          ? 'Bundle — Paperback'
          : language === 'ar'
          ? 'Before We Get Older — Arabic Paperback'
          : 'Before We Get Older — English Paperback'
      );
      return;
    }
    setFormat('paper');
    setQuantity(1);
    setCouponCode('');
    setCouponStatus(null);
    setCouponDiscount(0);
  };

  const handleSubmit = async () => {
    if (!book) return;
    if (showConfirmation) return;

    // Missing/invalid required fields are surfaced inline (see `errors` below and
    // the field-level styling each child form already applies), so we don't also
    // raise the loud server-error banner here — that banner is reserved for
    // actual submission failures further down.
    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const formData = new FormData();
      formData.append('firstName', firstName);
      formData.append('lastName', lastName);
      formData.append('email', email);
      formData.append('phone', phone);
      formData.append('bookTitle', book.title);
      formData.append('format', format);
      formData.append('digitalType', digitalType);
      formData.append('language', language);
      formData.append('quantity', quantity);
      formData.append('price', price);
      formData.append('wilaya', wilaya);
      formData.append('commune', commune);
      formData.append('deliveryMethod', deliveryMethod);
      formData.append('couponCode', couponCode);
      formData.append('proof', proof);

      const response = await fetch(`${API_URL}/orders/order`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Something went wrong. Please try again.');
      }

      setShowConfirmation(true);
    } catch (err) {
      if (!navigator.onLine) {
        setSubmitError('You appear to be offline. Please check your connection and try again.');
      } else {
        setSubmitError(err.message || 'Failed to submit order. Please try again or contact us on WhatsApp.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <section
        ref={orderSectionRef}
        className="order-section"
        style={{ '--accent-color': themeColor, '--accent-text': textColor }}
      >
        <div className="order-container">
          <div className="library-badge-row">
            <span className="library-badge">✦ Elby Books Library</span>
          </div>
          <h1 className="order-title">
            <span>Choose your next read</span>
            <img src={boxGift} alt="Gift box" className="order-title-icon" />
          </h1>
          <p className="order-subtitle">
            Pick a title from the shelf, choose your format, and it&apos;s yours.
          </p>

          <Bookselector books={BOOKS} bookId={bookId} onChange={handleBookChange} currency={CURRENCY} />

          {book && (
            <div className="order-layout">
              {/* LEFT */}
              <div className="order-left">
                {/* Cover, format pill and price now share one panel (matches the
                    reference), so the background/radius live on .hero-card and
                    Bookpreview's own .book-preview wrapper is just padding. */}
                <div
                  className="hero-card"
                  style={{ '--accent-color': themeColor, '--accent-text': textColor }}
                >
                  <Bookpreview
                    book={book}
                    format={format}
                    images={images}
                    imageIndex={imageIndex}
                    language={language}
                    onLanguageChange={handleLanguageChange}
                    bundleCovers={bundleCovers}
                  />

                  <Formatselector
                    book={book}
                    format={format}
                    quantity={quantity}
                    price={price}
                    couponDiscount={couponDiscount}
                    isPaperbackComingSoon={isPaperbackComingSoon}
                    onSelectDigital={handleSelectDigital}
                    onSelectPaper={handleSelectPaper}
                    onQuantityChange={setQuantity}
                  />
                </div>
              </div>

              {/* RIGHT */}
              <div className="order-right">
                <div className="order-summary-sticky">
                  <div className="order-summary-info">
                    <span className="order-summary-title">{book.title}</span>
                    <span className="order-summary-meta">
                      {format === 'digital'
                        ? `Digital · ${digitalType.toUpperCase()}`
                        : `Paperback × ${quantity}`}
                    </span>
                  </div>
                  <div className="order-summary-progress" aria-hidden="true">
                    <span className="summary-dot complete" title="Book chosen" />
                    <span className={`summary-dot ${detailsComplete ? 'complete' : ''}`} title="Your details" />
                    <span className={`summary-dot ${proofComplete ? 'complete' : ''}`} title="Payment proof" />
                  </div>
                  <div className="order-summary-price">
                    {price.toLocaleString()} {CURRENCY}
                  </div>
                </div>

                <p className="section-label">Your details</p>
                <Contactform
                  book={book}
                  format={format}
                  firstName={firstName}
                  onFirstNameChange={(e) => { setFirstName(e.target.value); clearError('firstName'); }}
                  lastName={lastName}
                  onLastNameChange={(e) => { setLastName(e.target.value); clearError('lastName'); }}
                  email={email}
                  onEmailChange={(e) => { setEmail(e.target.value); clearError('email'); }}
                  phone={phone}
                  onPhoneChange={(e) => { setPhone(e.target.value); clearError('phone'); }}
                  digitalType={digitalType}
                  onDigitalTypeChange={(e) => setDigitalType(e.target.value)}
                  couponCode={couponCode}
                  couponStatus={couponStatus}
                  couponDiscount={couponDiscount}
                  onCouponChange={handleCouponChange}
                  wilaya={wilaya}
                  onWilayaChange={(e) => { setWilaya(e.target.value); setCommune(''); clearError('wilaya'); }}
                  wilayaOptions={Object.keys(ALGERIA_LOCATIONS)}
                  commune={commune}
                  onCommuneChange={(e) => { setCommune(e.target.value); clearError('commune'); }}
                  communeOptions={communes}
                  deliveryMethod={deliveryMethod}
                  onDeliveryMethodChange={(e) => setDeliveryMethod(e.target.value)}
                  deliveryFee={deliveryFee}
                  errors={errors}
                />

                <Paymentbox />

                <p className="section-label">Proof of payment</p>
                <Uploadbox
                  proof={proof}
                  proofError={proofError}
                  hasError={errors.proof || proofError}
                  fileInputRef={fileInputRef}
                  onFileChange={handleFileChange}
                  onBoxClick={() => fileInputRef.current.click()}
                />

                {Object.keys(errors).length > 0 && !submitError && (
                  <div className="form-validation-hint">
                    Please complete the highlighted fields above before confirming.
                  </div>
                )}

                {submitError && <div className="submit-error">⚠️ {submitError}</div>}

                <button
                  className={`order-submit ${isSubmitting ? 'submitting' : ''} ${submitError ? 'error' : ''}`}
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting && <span className="spinner" />}
                  {isSubmitting ? 'Sending your order...' : submitError ? 'Try again' : 'Confirm order'}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <Bundlesection
        bundleCovers={bundleCovers}
        onChooseDigitalBundle={() => {
          setBookId('bundle');
          setFormat('digital');
          setQuantity(1);
          setDigitalType('pdf');
          setCouponCode('');
          setCouponStatus(null);
          setCouponDiscount(0);
          orderSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
        onPreorderPaperBundle={() => openPreorder('Bundle — Paperback')}
      />

      {showConfirmation && book && (
        <Orderconfirmationmodal
          book={book}
          format={format}
          language={language}
          digitalType={digitalType}
          quantity={quantity}
          price={price}
          email={email}
          commune={commune}
          wilaya={wilaya}
          bundleCovers={bundleCovers}
          onClose={closeConfirmation}
        />
      )}

      {showPreorder && (
        <Preordermodal
          firstName={preorderFirstName}
          onFirstNameChange={(e) => { setPreorderFirstName(e.target.value); setPreorderError(''); }}
          email={preorderEmail}
          onEmailChange={(e) => { setPreorderEmail(e.target.value); setPreorderError(''); }}
          bookLabel={preorderBook}
          submitted={preorderSubmitted}
          loading={preorderLoading}
          error={preorderError}
          onSubmit={handlePreorderSubmit}
          onClose={closePreorder}
        />
      )}

      <BeforeFooter />
    </React.Fragment>
  );
}

export default OrderPage;